import type { AppNotification, NotificationActionTarget, NotificationChannel, NotificationDelivery, NotificationFamily, NotificationPreferences } from '../types';

export const NOTIFICATION_DELIVERY_VERSION = 'phase4c-v1';

const allowedTargets = new Set<NotificationActionTarget>(['home', 'upcoming', 'records', 'reports', 'setup', 'health', 'monthlyClose', 'add']);
const familyByCategory: Record<string, NotificationFamily> = {
  payment: 'payments',
  budget: 'budgets',
  account: 'accounts',
  credit_card: 'accounts',
  reconciliation: 'reconciliation',
  monthly_close: 'reconciliation',
  health: 'health'
};

export function defaultNotificationPreferences(emailAddress = ''): NotificationPreferences {
  return {
    id: 'notifications',
    uid: '',
    inAppEnabled: true,
    pushEnabled: false,
    emailEnabled: false,
    emailAddress,
    pushPreview: 'private',
    quietHoursEnabled: false,
    quietStart: '22:30',
    quietEnd: '07:00',
    timezone: 'Asia/Kolkata',
    families: {
      payments: true,
      accounts: true,
      budgets: true,
      reconciliation: true,
      health: true
    }
  };
}

export function normalizeNotificationPreferences(input?: Partial<NotificationPreferences>, emailAddress = ''): NotificationPreferences {
  const defaults = defaultNotificationPreferences(emailAddress);
  return {
    ...defaults,
    ...input,
    emailAddress: input?.emailAddress || emailAddress,
    pushPreview: input?.pushPreview === 'detailed' ? 'detailed' : 'private',
    quietStart: /^\d{2}:\d{2}$/.test(input?.quietStart || '') ? String(input?.quietStart) : defaults.quietStart,
    quietEnd: /^\d{2}:\d{2}$/.test(input?.quietEnd || '') ? String(input?.quietEnd) : defaults.quietEnd,
    families: { ...defaults.families, ...(input?.families || {}) }
  };
}

export function notificationFamily(item: Pick<AppNotification, 'category' | 'type'>): NotificationFamily {
  return familyByCategory[String(item.category || item.type)] || 'health';
}

const minutes = (time: string) => {
  const [hours, mins] = time.split(':').map(Number);
  return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(mins) ? mins : 0);
};

export function isQuietTime(prefs: Pick<NotificationPreferences, 'quietHoursEnabled' | 'quietStart' | 'quietEnd'>, now = new Date()) {
  if (!prefs.quietHoursEnabled) return false;
  const start = minutes(prefs.quietStart);
  const end = minutes(prefs.quietEnd);
  const current = now.getHours() * 60 + now.getMinutes();
  return start <= end ? current >= start && current < end : current >= start || current < end;
}

export function allowedChannels(item: AppNotification, prefs: NotificationPreferences, now = new Date()): NotificationChannel[] {
  const family = notificationFamily(item);
  const channels: NotificationChannel[] = [];
  if (prefs.inAppEnabled || item.priority === 'critical') channels.push('inApp');
  if (!prefs.families[family]) return channels;

  const quiet = isQuietTime(prefs, now);
  const canPush = prefs.pushEnabled && (!quiet || item.priority === 'critical');
  const canEmail = prefs.emailEnabled && !!normalizeRecipientEmail(prefs.emailAddress);

  if (item.priority === 'critical') {
    if (canPush) channels.push('push');
    if (canEmail) channels.push('email');
  } else if (item.priority === 'high') {
    if (canPush) channels.push('push');
    if (canEmail && ['payments', 'accounts', 'reconciliation', 'health'].includes(family)) channels.push('email');
  } else if (item.priority === 'normal') {
    if (canPush && ['payments', 'budgets', 'accounts'].includes(family)) channels.push('push');
  }
  return channels;
}

export function deliveryKey(notificationKey: string, channel: Exclude<NotificationChannel, 'inApp'>) {
  return `${notificationKey}:${channel}`.replace(/[\/#?[\]]+/g, '_');
}

export function alreadyDelivered(deliveries: NotificationDelivery[], notificationKey: string, channel: Exclude<NotificationChannel, 'inApp'>) {
  const key = deliveryKey(notificationKey, channel);
  return deliveries.some((row) => (row.id === key || `${row.notificationKey}:${row.channel}` === key) && ['sent', 'pending'].includes(row.status));
}

export function safeActionTarget(target?: NotificationActionTarget) {
  return target && allowedTargets.has(target) ? target : 'home';
}

export function actionHash(target?: NotificationActionTarget) {
  const safe = safeActionTarget(target);
  if (safe === 'reports') return '#reports';
  if (safe === 'health') return '#health';
  if (safe === 'setup') return '#settings';
  if (safe === 'monthlyClose') return '#monthly-close';
  return `#${safe}`;
}

export function buildPushPayload(item: AppNotification, prefs: NotificationPreferences, origin = '') {
  const privateBody = 'You have a payment or finance alert requiring attention in SG-PFS.';
  return {
    title: item.priority === 'critical' ? 'SG-PFS critical alert' : 'SG-PFS notification',
    body: prefs.pushPreview === 'detailed' ? item.message : privateBody,
    data: {
      notificationKey: item.dedupeKey || item.id,
      target: safeActionTarget(item.action?.target),
      url: `${origin || ''}/${actionHash(item.action?.target)}`
    }
  };
}

export function normalizeRecipientEmail(email?: string) {
  const clean = String(email || '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean) ? clean : '';
}

export function buildEmailPayload(item: AppNotification, prefs: NotificationPreferences, origin = '') {
  const to = normalizeRecipientEmail(prefs.emailAddress);
  if (!to) return undefined;
  return {
    to,
    subject: `SG-PFS: ${item.title}`,
    text: `${item.title}\n\n${item.message}\n\nOpen SG-PFS: ${origin || ''}/${actionHash(item.action?.target)}\n\nYou can manage delivery preferences in SG-PFS Settings.`
  };
}

export function classifyPushSendResult(error: unknown): 'invalid-token' | 'transient' | 'permanent' {
  const code = String((error as { code?: unknown })?.code || (error as { errorInfo?: { code?: unknown } })?.errorInfo?.code || error || '').toLowerCase();
  if (code.includes('registration-token-not-registered') || code.includes('invalid-registration-token') || code.includes('messaging/invalid-argument')) return 'invalid-token';
  if (code.includes('unavailable') || code.includes('deadline') || code.includes('internal')) return 'transient';
  return 'permanent';
}

export function selectDeliveryAttempts(input: {
  notifications: AppNotification[];
  preferences: NotificationPreferences;
  deliveries: NotificationDelivery[];
  now?: Date;
}) {
  return input.notifications.flatMap((item) => {
    const key = item.dedupeKey || item.id;
    return allowedChannels(item, input.preferences, input.now)
      .filter((channel): channel is Exclude<NotificationChannel, 'inApp'> => channel !== 'inApp')
      .filter((channel) => !alreadyDelivered(input.deliveries, key, channel))
      .map((channel) => ({ notification: item, notificationKey: key, channel, deliveryId: deliveryKey(key, channel) }));
  });
}
